import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  Injector,
  NgModule
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  NOTIFICATION_CONFIG,
  SPINNER_COLOR
} from '@core-argos-angular-artifact/components';
import {
  AuthenticationConfig,
  AuthenticationModule,
  AuthenticationService,
  ConfigService
} from '@core-argos-angular-artifact/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { from, Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { getKcConfig } from '../configs/login.config';
import { SharedModule } from '../shared/shared.module';
import { ApplicationLandingComponent } from './components/application-landing/application-landing.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { RootComponent } from './components/root/root.component';
import { SidebarMainComponent } from './components/sidebar-main/sidebar-main.component';
import { UnsubscribeCommunicationsComponent } from './components/unsubscribe-communications/unsubscribe-communications.component';
import { UserCancelationComponent } from './components/user-cancelation/user-cancelation.component';
import { CoreRoutingModule } from './core-routing.module';
import { ErrorHandlerService } from './services/error-handler.service';
import { ResolveEndRouterService } from './services/resolve-end-router.service';

class BundledTranslateLoader extends TranslateHttpLoader {
  getTranslation(lang: string): Observable<any> {
    return from(import(`../../assets/i18n/${lang}.json`));
  }
}

export const createTranslateLoader = (http: HttpClient): any => {
  return new BundledTranslateLoader(http);
};

export const setInitApp = (injector: Injector) => {
  return (): any => {
    const service: ResolveEndRouterService = injector.get(
      ResolveEndRouterService
    );
    return service.setInitApp(true);
  };
};

const getConfigApp = (configService: ConfigService) => {
  return () => configService.getConfigFiles();
};

const initializeAuthService = (
  authentication: AuthenticationService,
  configService: ConfigService
) => {
  return () => {
    return configService.getAsyncConfig().pipe(
      first(),
      switchMap(config => {
        const kcConfig: AuthenticationConfig = getKcConfig(config);
        return authentication.init(kcConfig).pipe(
          map(data => data),
          catchError(ex => {
            console.log('Error ', ex);
            return of(null);
          })
        );
      }),
      catchError(ex => {
        console.log('Error ', ex);
        return of(null);
      })
    );
  };
};

@NgModule({
  declarations: [
    RootComponent,
    FooterComponent,
    HeaderComponent,
    NotFoundComponent,
    SidebarMainComponent,
    ApplicationLandingComponent,
    UserCancelationComponent,
    UnsubscribeCommunicationsComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    SharedModule,
    CoreRoutingModule,
    AuthenticationModule
  ],
  providers: [
    {
      provide: 'CONFIG_FILES',
      useValue: ['./assets/config/config.json']
    },
    {
      provide: NOTIFICATION_CONFIG,
      useValue: {
        timeout: 1100,
        theme: 'alert-class'
      }
    },
    {
      provide: SPINNER_COLOR,
      useValue: '#e60000'
    },
    {
      provide: APP_INITIALIZER,
      useFactory: getConfigApp,
      deps: [ConfigService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: setInitApp,
      deps: [Injector],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthService,
      multi: true,
      deps: [AuthenticationService, ConfigService]
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService
    }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CoreModule {}
